import * as THREE from 'three'
import { toMatrixTuple } from '../../imported-model/loaders/assembly-node-utils'
import type { GlbHierarchyNode } from '../../imported-model/loaders/glb'
import type {
  AssemblyManifest,
  AssemblyModelAsset,
  ComposedAssemblyResult,
  ImportedPartBodyBrepBinding,
  ImportedModelResult,
  ImportedTopologySource,
  Matrix4Tuple,
  PartAsset,
} from '../../imported-model/loaders/types'
import { buildImportedBrepExportDefinitionId } from '../../imported-model/hydration/imported-brep-geometry-object-id'
import type {
  StepExportAssemblyBodyRef,
  StepExportAssemblyContext,
  StepExportAssemblyNode,
  StepExportColor,
  StepExportSource,
} from './types'

/** STEP 导出用单位矩阵。 */
const IDENTITY_MATRIX: Matrix4Tuple = [
  1, 0, 0, 0,
  0, 1, 0, 0,
  0, 0, 1, 0,
  0, 0, 0, 1,
]

/** STEP 导出语义节点类型。 */
type ExportableHierarchyKind = Extract<NonNullable<GlbHierarchyNode['meta']>['semanticKind'], 'assembly' | 'part'>

/** 当前场景对象解析来源。 */
type StepSceneObjectResolutionSource =
  | 'hierarchy-object-id'
  | 'hierarchy-selection-object-id'
  | 'assembly-node-binding'
  | 'missing-object-fallback'

/** 递归构建装配快照时的只读索引。 */
interface StepSceneExportIndex {
  /** 零件资源索引，key 为后端提供的文件 UUID。 */
  readonly partAssetsByUuid: ReadonlyMap<string, PartAsset>
  /** 子装配资源索引，key 为后端提供的文件 UUID。 */
  readonly assemblyAssetsByUuid: ReadonlyMap<string, AssemblyModelAsset>
  /** 当前场景对象索引。 */
  readonly sceneObjectIndex: StepSceneObjectIndex
  /** hierarchy objectId 到 manifest nodeId 的反向索引。 */
  readonly manifestNodeIdByObjectId: ReadonlyMap<string, string>
  /** 当前装配 manifest。 */
  readonly manifest: AssemblyManifest
}

/** 递归构建装配快照时的可变上下文。 */
interface StepSceneBuildState extends StepSceneExportIndex {
  /** 当前导出源的稳定作用域。 */
  readonly scope: string
  /** 最终写入 Worker 的装配节点列表。 */
  readonly nodes: StepExportAssemblyNode[]
}

/** 当前场景对象索引。 */
interface StepSceneObjectIndex {
  /** 按 Object3D UUID 查找当前对象。 */
  readonly byUuid: ReadonlyMap<string, THREE.Object3D>
  /** 按 assemblyNodeId 查找当前对象。 */
  readonly byAssemblyNodeId: ReadonlyMap<string, readonly THREE.Object3D[]>
}

/** 当前场景对象解析结果。 */
interface StepSceneObjectResolution {
  /** 命中的当前 Three.js 对象。 */
  readonly object: THREE.Object3D | null
  /** transform 来源。 */
  readonly source: StepSceneObjectResolutionSource
}

/** STEP 导出源创建选项。 */
export interface CreateWorkerStateStepExportSourceOptions {
  /** 当前 Viewer 场景根；提供时以当前场景树为装配位姿权威来源。 */
  readonly sceneRoot?: THREE.Object3D | null
}

/**
 * 判断导入结果是否为装配组合结果。
 *
 * @param result 导入模型结果。
 * @returns 是否为组合装配结果。
 */
function isComposedAssemblyResult(
  result: ImportedModelResult | null | undefined,
): result is ComposedAssemblyResult {
  return result?.kind === 'composed-assembly'
}

/**
 * 规范化可拼入节点 ID 的字符串片段。
 *
 * @param value 原始字符串。
 * @returns 安全的 ID 片段。
 */
function sanitizeIdSegment(value: string): string {
  const normalized = value.trim().replace(/[^a-zA-Z0-9._:-]/g, '_')
  return normalized.length > 0 ? normalized : 'unknown'
}

/**
 * 读取 hierarchy 节点是否是导出语义组件。
 *
 * @param node 设计树 hierarchy 节点。
 * @returns assembly / part 语义；非组件根返回 undefined。
 */
function readExportableHierarchyKind(node: GlbHierarchyNode): ExportableHierarchyKind | undefined {
  const semanticKind = node.meta?.semanticKind
  return semanticKind === 'assembly' || semanticKind === 'part' ? semanticKind : undefined
}

/**
 * 读取 hierarchy 节点对应的外部零件或子装配资源 UUID。
 *
 * @param node 设计树 hierarchy 节点。
 * @returns 资源 UUID。
 */
function readHierarchyExternalFileUuid(node: GlbHierarchyNode): string | undefined {
  const externalFileUuid = node.meta?.externalFileUuid?.trim()
  return externalFileUuid && externalFileUuid.length > 0 ? externalFileUuid : undefined
}

/**
 * 计算对象相对于父导出对象的完整 4x4 矩阵。
 *
 * 设计说明：
 * 导出时必须读取当前场景对象的 matrixWorld，而不是导入 manifest 中的旧 transform。
 * 这样用户在编辑器中移动、旋转或缩放零部件后，STEP 中的实例位姿才与当前视口一致。
 *
 * @param object 当前导出组件对象。
 * @param parentObject 父导出组件对象；根级组件为 null。
 * @returns 相对父组件的列主序 4x4 矩阵。
 */
function readRelativeWorldMatrix(
  object: THREE.Object3D,
  parentObject: THREE.Object3D | null,
): Matrix4Tuple {
  object.updateWorldMatrix(true, false)
  parentObject?.updateWorldMatrix(true, false)

  if (!parentObject) {
    return toMatrixTuple(object.matrixWorld)
  }

  const relativeMatrix = new THREE.Matrix4()
    .copy(parentObject.matrixWorld)
    .invert()
    .multiply(object.matrixWorld)

  return toMatrixTuple(relativeMatrix)
}

/**
 * 读取对象 userData 中的字符串字段。
 *
 * @param record userData 或嵌套记录。
 * @param key 字段名。
 * @returns 去空白后的字符串。
 */
function readUserDataString(record: unknown, key: string): string | undefined {
  if (!record || typeof record !== 'object') {
    return undefined
  }

  const value = (record as Record<string, unknown>)[key]
  if (typeof value !== 'string') {
    return undefined
  }

  const normalized = value.trim()
  return normalized.length > 0 ? normalized : undefined
}

/**
 * 读取材质中可导出到 STEP/XCAF 的实体颜色。
 *
 * @param material GLB visible mesh 材质。
 * @returns STEP 导出颜色；材质没有显式颜色时返回 undefined。
 */
function readMaterialStepExportColor(material: THREE.Material | null | undefined): StepExportColor | undefined {
  if (!material || material.visible === false) {
    return undefined
  }

  const materialWithColor = material as THREE.Material & {
    color?: THREE.Color
    opacity?: number
  }
  if (!(materialWithColor.color instanceof THREE.Color)) {
    return undefined
  }

  return [
    materialWithColor.color.r,
    materialWithColor.color.g,
    materialWithColor.color.b,
    typeof materialWithColor.opacity === 'number' ? materialWithColor.opacity : 1,
  ]
}

/**
 * 读取 mesh 上某个 primitive 对应的材质。
 *
 * @param mesh GLB visible mesh。
 * @param materialIndex glTF primitive 材质索引。
 * @returns 对应材质；无法精确命中时返回首个材质作为 body 级颜色兜底。
 */
function resolvePrimitiveMaterial(mesh: THREE.Mesh, materialIndex: number | undefined): THREE.Material | undefined {
  const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
  if (materials.length === 0) {
    return undefined
  }

  if (typeof materialIndex === 'number' && materialIndex >= 0) {
    return materials[materialIndex] ?? materials[0]
  }

  return materials[0]
}

/**
 * 按 partAssetLocal.meshId 建立 GLB visible mesh 索引。
 *
 * @param renderRoot 当前零件或装配局部几何的可见模板根。
 * @returns meshId 到 visible mesh 的索引。
 */
function createTemplateMeshIndex(renderRoot: THREE.Object3D | undefined): ReadonlyMap<string, THREE.Mesh> {
  const meshByMeshId = new Map<string, THREE.Mesh>()
  if (!renderRoot) {
    return meshByMeshId
  }

  renderRoot.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) {
      return
    }
    const meshId = object.userData?.partAssetLocal?.meshId
    if (typeof meshId === 'string' && meshId.length > 0 && !meshByMeshId.has(meshId)) {
      meshByMeshId.set(meshId, object)
    }
  })
  return meshByMeshId
}

/**
 * 从 GLB visible mesh 中解析 body 级 STEP 颜色。
 *
 * 设计说明：
 * 1. topo sidecar 只提供拓扑语义，不承载显示颜色；颜色权威来源仍是 GLB 材质；
 * 2. 当前 native bridge 只暴露 XCAF label 级颜色写入，因此这里收敛为 body 级颜色；
 * 3. 若一个 body 横跨多个 primitive 且颜色不同，优先使用第一个明确材质颜色，后续如需
 *    精确到 face/subshape，可在 StepExportAssemblyBodyRef 上扩展子形状颜色表。
 *
 * @param source 导入拓扑源。
 * @param binding body 绑定。
 * @param renderRoot 可见模板根。
 * @returns body 颜色。
 */
function resolveBodyBindingStepExportColor(
  source: ImportedTopologySource,
  binding: ImportedPartBodyBrepBinding,
  meshByMeshId: ReadonlyMap<string, THREE.Mesh>,
): StepExportColor | undefined {
  for (const primitiveId of binding.primitiveIds) {
    const primitive = source.primitiveCatalog[primitiveId]
    if (!primitive) {
      continue
    }

    const mesh = meshByMeshId.get(primitive.meshId)
    if (!mesh) {
      continue
    }

    const color = readMaterialStepExportColor(resolvePrimitiveMaterial(mesh, primitive.materialIndex))
    if (color) {
      return color
    }
  }

  return undefined
}

/**
 * 向多值索引追加对象。
 *
 * @param index 目标索引。
 * @param key 索引 key。
 * @param object 当前场景对象。
 */
function appendObjectIndexValue(
  index: Map<string, THREE.Object3D[]>,
  key: string | undefined,
  object: THREE.Object3D,
): void {
  if (!key) {
    return
  }

  const list = index.get(key) ?? []
  if (!list.includes(object)) {
    list.push(object)
  }
  index.set(key, list)
}

/**
 * 收集对象上可作为装配节点 ID 的运行时元数据。
 *
 * 设计说明：
 * 真实编辑命令操作的通常是选择代理/实例根，而不是带 `glbHierarchy` 的可见 mesh。
 * 导出时必须把这些代理对象按 assemblyNodeId 建入索引，才能读取用户最新移动后的矩阵。
 *
 * @param object 当前场景对象。
 * @returns 装配节点 ID 集合。
 */
function collectRuntimeAssemblyNodeIds(object: THREE.Object3D): string[] {
  const selectionTarget = object.userData?.importedSelectionTarget as Record<string, unknown> | undefined
  return [
    readUserDataString(object.userData, 'assemblyNodeId'),
    readUserDataString(selectionTarget, 'assemblyNodeId'),
  ].filter((value): value is string => Boolean(value))
}

/**
 * 判断对象是否是导入装配实例的选择代理根。
 *
 * @param object 当前场景对象。
 * @returns 是否为代理根。
 */
function isImportedSelectionProxyRoot(object: THREE.Object3D): boolean {
  return readUserDataString(object.userData, 'importedSelectionProxyObjectId') === object.uuid
}

/**
 * 在同一个 assemblyNodeId 的候选对象中选择最适合承载 transform 的对象。
 *
 * @param candidates 候选对象列表。
 * @returns 当前应读取矩阵的对象。
 */
function selectPreferredAssemblyObject(candidates: readonly THREE.Object3D[]): THREE.Object3D | null {
  return candidates.find(isImportedSelectionProxyRoot)
    ?? candidates.find((object) => object.parent !== null)
    ?? candidates[0]
    ?? null
}

/**
 * 创建当前场景对象索引。
 *
 * @param result 当前装配结果。
 * @param sceneRoot Viewer 场景根。
 * @returns 可用于导出前定位当前 Object3D 的索引。
 */
function createSceneObjectIndex(
  result: ComposedAssemblyResult,
  sceneRoot: THREE.Object3D | null | undefined,
): StepSceneObjectIndex {
  const byUuid = new Map<string, THREE.Object3D>()
  const byAssemblyNodeId = new Map<string, THREE.Object3D[]>()
  const appendObject = (object: THREE.Object3D) => {
    byUuid.set(object.uuid, object)
    collectRuntimeAssemblyNodeIds(object).forEach((assemblyNodeId) => {
      appendObjectIndexValue(byAssemblyNodeId, assemblyNodeId, object)
    })
  }

  /**
   * Viewer 场景是当前编辑后的权威对象树；result.root 和 sceneBindings 作为兜底，
   * 避免测试或非 Viewer 宿主没有传入 sceneRoot 时丢失 live 对象。
   */
  const traversalRoots = [sceneRoot, result.root]
  traversalRoots.forEach((root) => {
    root?.traverse((object) => appendObject(object))
  })
  Object.values(result.sceneBindings.byObject3DUuid).forEach((object) => appendObject(object))
  Object.entries(result.sceneBindings.byAssemblyNodeId).forEach(([assemblyNodeId, objectIds]) => {
    objectIds.forEach((objectId) => {
      const object = byUuid.get(objectId) ?? result.sceneBindings.byObject3DUuid[objectId]
      if (object) {
        appendObjectIndexValue(byAssemblyNodeId, assemblyNodeId, object)
      }
    })
  })

  return { byUuid, byAssemblyNodeId }
}

/**
 * 建立 hierarchy objectId 到 manifest nodeId 的反向索引。
 *
 * @param result 当前装配结果。
 * @returns objectId 到 manifest nodeId 的映射。
 */
function createManifestNodeIdByObjectId(result: ComposedAssemblyResult): Map<string, string> {
  const resultMap = new Map<string, string>()
  Object.entries(result.sceneBindings.byAssemblyNodeId).forEach(([assemblyNodeId, objectIds]) => {
    objectIds.forEach((objectId) => {
      resultMap.set(objectId, assemblyNodeId)
    })
  })
  return resultMap
}

/**
 * 解析 hierarchy 节点当前对应的 Three.js 对象。
 *
 * @param state 构建上下文。
 * @param node hierarchy 节点。
 * @returns 当前场景对象解析结果。
 */
function resolveHierarchySceneObject(
  state: StepSceneExportIndex,
  node: GlbHierarchyNode,
): StepSceneObjectResolution {
  const directObject = state.sceneObjectIndex.byUuid.get(node.objectId)
  if (directObject) {
    return { object: directObject, source: 'hierarchy-object-id' }
  }

  const selectionObjectId = node.meta?.selectionObjectId?.trim()
  if (selectionObjectId) {
    const selectionObject = state.sceneObjectIndex.byUuid.get(selectionObjectId)
    if (selectionObject) {
      return { object: selectionObject, source: 'hierarchy-selection-object-id' }
    }
  }

  const boundObject = selectPreferredAssemblyObject(
    state.sceneObjectIndex.byAssemblyNodeId.get(node.objectId) ?? [],
  )
  if (boundObject) {
    return { object: boundObject, source: 'assembly-node-binding' }
  }

  return { object: null, source: 'missing-object-fallback' }
}

/**
 * 从 manifest 读取兜底 transform。
 *
 * @param state 构建上下文。
 * @param hierarchyObjectId hierarchy objectId。
 * @returns manifest 中记录的旧 transform；缺失时返回 undefined。
 */
function readManifestFallbackTransform(
  state: StepSceneExportIndex,
  hierarchyObjectId: string,
): Matrix4Tuple | undefined {
  const manifestNodeId = state.manifestNodeIdByObjectId.get(hierarchyObjectId)
    ?? (state.manifest.nodesById[hierarchyObjectId] ? hierarchyObjectId : undefined)
  if (!manifestNodeId) {
    return undefined
  }

  return state.manifest.nodesById[manifestNodeId]?.transform
}

/**
 * 读取 hierarchy 节点的导出 transform。
 *
 * @param state 构建上下文。
 * @param node hierarchy 节点。
 * @param resolvedObject 当前场景对象解析结果。
 * @param parentObject 父导出对象。
 * @returns transform 及其来源。
 */
function resolveHierarchyNodeTransform(
  state: StepSceneExportIndex,
  node: GlbHierarchyNode,
  resolvedObject: StepSceneObjectResolution,
  parentObject: THREE.Object3D | null,
): { transform: Matrix4Tuple; source: StepSceneObjectResolutionSource | 'manifest-fallback' } {
  if (resolvedObject.object) {
    return {
      transform: readRelativeWorldMatrix(resolvedObject.object, parentObject),
      source: resolvedObject.source,
    }
  }

  const manifestTransform = readManifestFallbackTransform(state, node.objectId)
  if (manifestTransform) {
    return {
      transform: manifestTransform,
      source: 'manifest-fallback',
    }
  }

  return {
    transform: IDENTITY_MATRIX,
    source: 'missing-object-fallback',
  }
}

/**
 * 创建装配节点诊断信息。
 *
 * @param node hierarchy 节点。
 * @param resolvedObject 当前场景对象解析结果。
 * @param transformSource transform 来源。
 * @returns 导出诊断信息。
 */
function createNodeDiagnostics(
  node: GlbHierarchyNode,
  resolvedObject: StepSceneObjectResolution,
  transformSource: NonNullable<StepExportAssemblyNode['diagnostics']>['transformSource'],
): StepExportAssemblyNode['diagnostics'] {
  return {
    hierarchyObjectId: node.objectId,
    hierarchyPath: node.path,
    sceneObjectId: resolvedObject.object?.uuid,
    sceneObjectName: resolvedObject.object?.name,
    transformSource,
  }
}

/**
 * 按 bodyId 建立 body 级 BRep 绑定索引。
 *
 * @param source 导入拓扑源。
 * @returns bodyId 到 body 级绑定数组的索引。
 */
function groupBodyBindingsByBodyId(
  source: ImportedTopologySource,
): Map<string, NonNullable<ImportedTopologySource['bodyBrepBindings']>[string][]> {
  const bindingsByBodyId = new Map<string, NonNullable<ImportedTopologySource['bodyBrepBindings']>[string][]>()
  Object.values(source.bodyBrepBindings ?? {}).forEach((binding) => {
    const bodyId = binding.bodyId?.trim()
    if (!bodyId) {
      return
    }

    const bindings = bindingsByBodyId.get(bodyId) ?? []
    bindings.push(binding)
    bindingsByBodyId.set(bodyId, bindings)
  })
  return bindingsByBodyId
}

/**
 * 判断 body 绑定是否携带可作为导出定义身份的 BRep 引用。
 *
 * @param binding body 级绑定。
 * @returns 绑定具备 BRep 引用时返回 true。
 */
function hasExportableBrepRef(
  binding: ImportedPartBodyBrepBinding,
): binding is ImportedPartBodyBrepBinding & { brepRef: NonNullable<ImportedPartBodyBrepBinding['brepRef']> } {
  return Boolean(binding.brepRef?.sha256)
}

/**
 * 解析 STEP 导出定义 ID。
 *
 * 设计说明：
 * topo sidecar 只负责交互拓扑，STEP 写出必须按 BRep payload 的稳定内容身份复用几何定义；
 * 因此优先使用 hydration 阶段写入的 `exportDefinitionId`，缺失时才从当前绑定的 `brepRef.sha256`
 * 现场推导，避免测试或旧缓存中存在 Shape objectId 但缺少定义 ID 时重新退化为按 objectId 重复写出。
 *
 * @param binding body 级绑定。
 * @param asset 当前 body 的 topology asset。
 * @returns 稳定导出定义 ID；缺失 BRep 引用时返回 undefined。
 */
function resolveBodyExportDefinitionId(
  binding: ImportedPartBodyBrepBinding,
  asset: NonNullable<ImportedTopologySource['bodyTopologyAssets']>[string],
): string | undefined {
  const assetDefinitionId = asset.exportDefinitionId?.trim()
  if (assetDefinitionId) {
    return assetDefinitionId
  }

  return hasExportableBrepRef(binding)
    ? buildImportedBrepExportDefinitionId(binding)
    : undefined
}

/**
 * 从导入拓扑源中收集 Worker 当前 shape 的 body 引用。
 *
 * @param source 导入拓扑源。
 * @returns STEP 装配快照中的 body 引用。
 */
function collectBodyRefsFromTopologySource(
  source: ImportedTopologySource,
  renderRoot?: THREE.Object3D,
): StepExportAssemblyBodyRef[] {
  const topologyAssets = source.bodyTopologyAssets ?? {}
  const bindingsByBodyId = groupBodyBindingsByBodyId(source)
  const meshByMeshId = createTemplateMeshIndex(renderRoot)
  const seenObjectIds = new Set<string>()
  const bodyRefs: StepExportAssemblyBodyRef[] = []

  source.bodyEntries.forEach((entry) => {
    const bindings = bindingsByBodyId.get(entry.bodyId) ?? []
    const bodyRef = bindings
      .map((binding) => {
        const asset = topologyAssets[binding.bodyBindingId]
        const geometryObjectId = asset?.geometryObjectId?.trim()
        if (!asset || !geometryObjectId) {
          return null
        }
        const exportDefinitionId = resolveBodyExportDefinitionId(binding, asset)
        const color = resolveBodyBindingStepExportColor(source, binding, meshByMeshId)

        return {
          objectId: geometryObjectId,
          ...(exportDefinitionId ? { exportDefinitionId } : {}),
          ...(color ? { color } : {}),
          name: entry.label.trim().length > 0 ? entry.label.trim() : entry.bodyId,
        }
      })
      .find((candidate): candidate is StepExportAssemblyBodyRef => candidate !== null)

    if (!bodyRef || seenObjectIds.has(bodyRef.objectId)) {
      return
    }

    seenObjectIds.add(bodyRef.objectId)
    bodyRefs.push(bodyRef)
  })

  if (bodyRefs.length > 0) {
    return bodyRefs
  }

  Object.values(topologyAssets).forEach((asset) => {
    const objectId = asset.geometryObjectId?.trim()
    if (!objectId || seenObjectIds.has(objectId)) {
      return
    }

    seenObjectIds.add(objectId)
    const exportDefinitionId = asset.exportDefinitionId?.trim()
    const binding = source.bodyBrepBindings?.[asset.bodyBindingId]
    const color = binding
      ? resolveBodyBindingStepExportColor(source, binding, meshByMeshId)
      : undefined
    bodyRefs.push({
      objectId,
      ...(exportDefinitionId ? { exportDefinitionId } : {}),
      ...(color ? { color } : {}),
      name: asset.bodyBindingId,
    })
  })

  return bodyRefs
}

/**
 * 递归收集子装配资源中的零件和装配索引。
 *
 * @param assemblyAsset 子装配资源。
 * @param partAssetsByUuid 零件索引。
 * @param assemblyAssetsByUuid 装配索引。
 * @param visitedAssemblyUuids 已访问的装配 UUID。
 */
function collectNestedAssemblyAssets(
  assemblyAsset: AssemblyModelAsset,
  partAssetsByUuid: Map<string, PartAsset>,
  assemblyAssetsByUuid: Map<string, AssemblyModelAsset>,
  visitedAssemblyUuids: Set<string>,
): void {
  if (visitedAssemblyUuids.has(assemblyAsset.fileUuid)) {
    return
  }

  visitedAssemblyUuids.add(assemblyAsset.fileUuid)
  assemblyAssetsByUuid.set(assemblyAsset.fileUuid, assemblyAsset)
  Object.values(assemblyAsset.partAssetsByUuid).forEach((partAsset) => {
    partAssetsByUuid.set(partAsset.fileUuid, partAsset)
  })
  Object.values(assemblyAsset.assemblyAssetsByUuid).forEach((childAssemblyAsset) => {
    collectNestedAssemblyAssets(
      childAssemblyAsset,
      partAssetsByUuid,
      assemblyAssetsByUuid,
      visitedAssemblyUuids,
    )
  })
}

/**
 * 为当前装配结果建立资源索引。
 *
 * @param result 当前装配结果。
 * @returns 零件与子装配资源索引。
 */
function createSceneExportIndex(
  result: ComposedAssemblyResult,
  sceneRoot: THREE.Object3D | null | undefined,
): StepSceneExportIndex {
  const partAssetsByUuid = new Map<string, PartAsset>()
  const assemblyAssetsByUuid = new Map<string, AssemblyModelAsset>()
  Object.values(result.partAssetsByUuid).forEach((partAsset) => {
    partAssetsByUuid.set(partAsset.fileUuid, partAsset)
  })
  Object.values(result.assemblyAssetsByUuid).forEach((assemblyAsset) => {
    collectNestedAssemblyAssets(
      assemblyAsset,
      partAssetsByUuid,
      assemblyAssetsByUuid,
      new Set<string>(),
    )
  })

  return {
    partAssetsByUuid,
    assemblyAssetsByUuid,
    sceneObjectIndex: createSceneObjectIndex(result, sceneRoot),
    manifestNodeIdByObjectId: createManifestNodeIdByObjectId(result),
    manifest: result.manifest,
  }
}

/**
 * 解析导出作用域。
 *
 * @param result 当前装配结果。
 * @returns 稳定作用域。
 */
function resolveExportScope(result: ComposedAssemblyResult): string {
  const fileUuid = result.fileInfo.uuid?.trim()
  if (fileUuid) {
    return sanitizeIdSegment(fileUuid)
  }

  return sanitizeIdSegment(result.fileInfo.fileId || result.partName)
}

/**
 * 创建 hierarchy 节点对应的装配节点 ID。
 *
 * @param scope 当前导出作用域。
 * @param node hierarchy 节点。
 * @returns 装配快照节点 ID。
 */
function createHierarchyNodeId(scope: string, node: GlbHierarchyNode): string {
  return `${scope}/hierarchy/${sanitizeIdSegment(node.objectId)}`
}

/**
 * 创建 assembly 节点自身局部几何对应的 part 节点。
 *
 * @param parentNode 父 assembly 节点。
 * @param topologySource 局部几何拓扑源。
 * @returns 局部几何 part 节点；无 body 时返回 undefined。
 */
function createAssemblyLocalGeometryNode(
  parentNode: StepExportAssemblyNode,
  topologySource: ImportedTopologySource | undefined,
  renderRoot?: THREE.Object3D,
): StepExportAssemblyNode | undefined {
  if (!topologySource) {
    return undefined
  }

  const bodies = collectBodyRefsFromTopologySource(topologySource, renderRoot)
  if (bodies.length === 0) {
    return undefined
  }

  return {
    nodeId: `${parentNode.nodeId}/local-geometry`,
    parentNodeId: parentNode.nodeId,
    name: `${parentNode.name} Geometry`,
    semanticKind: 'part',
    childNodeIds: [],
    transform: IDENTITY_MATRIX,
    partId: `${parentNode.nodeId}/local-geometry`,
    bodies,
  }
}

/**
 * 把节点挂到父节点下并写入节点列表。
 *
 * @param parentNode 父节点。
 * @param node 当前节点。
 * @param nodes 全部节点列表。
 */
function appendNode(
  parentNode: StepExportAssemblyNode,
  node: StepExportAssemblyNode,
  nodes: StepExportAssemblyNode[],
): void {
  parentNode.childNodeIds.push(node.nodeId)
  nodes.push(node)
}

/**
 * 创建 part hierarchy 节点。
 *
 * @param state 构建上下文。
 * @param hierarchyNode hierarchy 节点。
 * @param parentNode 父装配节点。
 * @param parentObject 父导出场景对象。
 * @param resolvedObject 当前场景对象解析结果。
 * @returns part 节点；找不到 live body 引用时返回 undefined。
 */
function createPartHierarchyNode(
  state: StepSceneBuildState,
  hierarchyNode: GlbHierarchyNode,
  parentNode: StepExportAssemblyNode,
  parentObject: THREE.Object3D | null,
  resolvedObject: StepSceneObjectResolution,
): StepExportAssemblyNode | undefined {
  const externalFileUuid = readHierarchyExternalFileUuid(hierarchyNode)
  const partAsset = externalFileUuid ? state.partAssetsByUuid.get(externalFileUuid) : undefined
  const bodies = partAsset ? collectBodyRefsFromTopologySource(partAsset, partAsset.renderTemplate) : []

  if (bodies.length === 0) {
    return undefined
  }

  const resolvedTransform = resolveHierarchyNodeTransform(
    state,
    hierarchyNode,
    resolvedObject,
    parentObject,
  )

  return {
    nodeId: createHierarchyNodeId(state.scope, hierarchyNode),
    parentNodeId: parentNode.nodeId,
    name: hierarchyNode.name,
    semanticKind: 'part',
    childNodeIds: [],
    transform: resolvedTransform.transform,
    partId: externalFileUuid
      ? `part:${sanitizeIdSegment(externalFileUuid)}`
      : `hierarchy:${sanitizeIdSegment(hierarchyNode.objectId)}`,
    bodies,
    diagnostics: createNodeDiagnostics(hierarchyNode, resolvedObject, resolvedTransform.source),
  }
}

/**
 * 创建 assembly hierarchy 节点。
 *
 * @param state 构建上下文。
 * @param hierarchyNode hierarchy 节点。
 * @param parentNode 父装配节点。
 * @param parentObject 父导出场景对象。
 * @param resolvedObject 当前场景对象解析结果。
 * @returns assembly 节点。
 */
function createAssemblyHierarchyNode(
  state: StepSceneBuildState,
  hierarchyNode: GlbHierarchyNode,
  parentNode: StepExportAssemblyNode,
  parentObject: THREE.Object3D | null,
  resolvedObject: StepSceneObjectResolution,
): StepExportAssemblyNode {
  const resolvedTransform = resolveHierarchyNodeTransform(
    state,
    hierarchyNode,
    resolvedObject,
    parentObject,
  )

  return {
    nodeId: createHierarchyNodeId(state.scope, hierarchyNode),
    parentNodeId: parentNode.nodeId,
    name: hierarchyNode.name,
    semanticKind: 'assembly',
    childNodeIds: [],
    transform: resolvedTransform.transform,
    diagnostics: createNodeDiagnostics(hierarchyNode, resolvedObject, resolvedTransform.source),
  }
}

/**
 * 在 assembly hierarchy 节点下补充它自身携带的局部几何。
 *
 * @param state 构建上下文。
 * @param hierarchyNode hierarchy 节点。
 * @param assemblyNode assembly 节点。
 */
function appendAssemblyLocalGeometry(
  state: StepSceneBuildState,
  hierarchyNode: GlbHierarchyNode,
  assemblyNode: StepExportAssemblyNode,
): void {
  const externalFileUuid = readHierarchyExternalFileUuid(hierarchyNode)
  const assemblyAsset = externalFileUuid ? state.assemblyAssetsByUuid.get(externalFileUuid) : undefined
  const localGeometryNode = createAssemblyLocalGeometryNode(
    assemblyNode,
    assemblyAsset?.localTopologySource,
    assemblyAsset?.sceneTemplate,
  )
  if (!localGeometryNode) {
    return
  }

  appendNode(assemblyNode, localGeometryNode, state.nodes)
}

/**
 * 递归追加 hierarchy 子节点。
 *
 * @param state 构建上下文。
 * @param parentNode 当前导出父节点。
 * @param parentObject 当前导出父对象。
 * @param hierarchyNode 当前 hierarchy 节点。
 */
function appendHierarchyChildren(
  state: StepSceneBuildState,
  parentNode: StepExportAssemblyNode,
  parentObject: THREE.Object3D | null,
  hierarchyNode: GlbHierarchyNode,
): void {
  hierarchyNode.children.forEach((child) => {
    appendHierarchyNode(state, parentNode, parentObject, child)
  })
}

/**
 * 将当前设计树 hierarchy 节点投影为 STEP 装配节点。
 *
 * 设计说明：
 * 1. hierarchy/manifest 只决定“导出哪些语义节点”；
 * 2. 节点 transform 必须通过当前场景索引读取 Object3D.matrixWorld；
 * 3. 这样装配编辑产生的位置、旋转、缩放会在导出前统一收口到 STEP 快照。
 *
 * @param state 构建上下文。
 * @param parentNode 当前导出父节点。
 * @param parentObject 当前导出父对象。
 * @param hierarchyNode 当前 hierarchy 节点。
 */
function appendHierarchyNode(
  state: StepSceneBuildState,
  parentNode: StepExportAssemblyNode,
  parentObject: THREE.Object3D | null,
  hierarchyNode: GlbHierarchyNode,
): void {
  const semanticKind = readExportableHierarchyKind(hierarchyNode)
  const resolvedObject = resolveHierarchySceneObject(state, hierarchyNode)

  if (!semanticKind) {
    appendHierarchyChildren(state, parentNode, parentObject, hierarchyNode)
    return
  }

  if (semanticKind === 'part') {
    const partNode = createPartHierarchyNode(
      state,
      hierarchyNode,
      parentNode,
      parentObject,
      resolvedObject,
    )
    if (partNode) {
      appendNode(parentNode, partNode, state.nodes)
      return
    }

    appendHierarchyChildren(state, parentNode, parentObject, hierarchyNode)
    return
  }

  const assemblyNode = createAssemblyHierarchyNode(
    state,
    hierarchyNode,
    parentNode,
    parentObject,
    resolvedObject,
  )
  appendNode(parentNode, assemblyNode, state.nodes)
  appendAssemblyLocalGeometry(state, hierarchyNode, assemblyNode)
  appendHierarchyChildren(
    state,
    assemblyNode,
    resolvedObject.object ?? parentObject,
    hierarchyNode,
  )
}

/**
 * 从当前设计树和 Three.js 场景矩阵创建 STEP 装配上下文。
 *
 * @param result 当前装配结果。
 * @returns STEP 装配上下文。
 */
export function createStepExportAssemblyContext(
  result: ComposedAssemblyResult,
  options: CreateWorkerStateStepExportSourceOptions = {},
): StepExportAssemblyContext | undefined {
  const traversalRoot = options.sceneRoot ?? result.root
  traversalRoot.updateWorldMatrix(true, true)
  result.root.updateWorldMatrix(true, true)

  const scope = resolveExportScope(result)
  const rootNode: StepExportAssemblyNode = {
    nodeId: `${scope}/root`,
    parentNodeId: null,
    name: result.partName,
    semanticKind: 'assembly',
    childNodeIds: [],
    transform: IDENTITY_MATRIX,
  }
  const state: StepSceneBuildState = {
    scope,
    nodes: [rootNode],
    ...createSceneExportIndex(result, traversalRoot),
  }

  appendHierarchyNode(state, rootNode, null, result.hierarchy)

  const hasReferencedBody = state.nodes.some((node) => (node.bodies?.length ?? 0) > 0)
  if (!hasReferencedBody) {
    return undefined
  }

  return {
    rootName: result.partName,
    rootNodeId: rootNode.nodeId,
    nodes: state.nodes,
  }
}

/**
 * 创建 Worker 当前几何状态 STEP 导出源。
 *
 * @param result 当前导入模型结果；缺省时按零件建模状态导出 Worker 全部 shape。
 * @returns STEP 导出源。
 */
export function createWorkerStateStepExportSource(
  result?: ImportedModelResult | null,
  options: CreateWorkerStateStepExportSourceOptions = {},
): StepExportSource {
  if (!isComposedAssemblyResult(result)) {
    return { kind: 'worker-state' }
  }

  const assemblyContext = createStepExportAssemblyContext(result, options)
  return assemblyContext
    ? { kind: 'worker-state', assemblyContext }
    : { kind: 'worker-state' }
}
